import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/AppShell';
import { PageHeader, EmptyState, ActionLink } from '@/components/visual/PagePrimitives';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/hooks/useLocale';
import { LocaleLink } from '@/components/LocaleLink';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, FileText, Calculator, AlertTriangle, GitCompareArrows } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { generateEstimatePDF } from '@/lib/pdfExport';
import { tcoOverridesOf } from '@/lib/assumptions';
import { EstimateCard } from '@/components/estimates/EstimateCard';
import { CompareEstimatesDialog } from '@/components/estimates/CompareEstimatesDialog';
import { ShareEstimateDialog } from '@/components/estimates/ShareEstimateDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import logger from '@/lib/logger';

const API = '/api';

export default function SavedEstimatesPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { localePath } = useLocale();
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [sharing, setSharing] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const isDemo = user?.is_demo;

  const fetchEstimates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/estimates`, {
        withCredentials: true,
      });
      setEstimates(res.data);
    } catch (err) {
      logger.warn('[SavedEstimatesPage] Failed to load estimates:', err?.message || err);
      toast.error(t('estimates.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchEstimates(); }, [fetchEstimates]);

  const handleDuplicate = async (id) => {
    try {
      const { data } = await axios.post(`${API}/estimates/${id}/duplicate`, {}, { withCredentials: true });
      toast.success(t('estimates.duplicated', { name: data.name }));
      fetchEstimates();
    } catch (err) {
      toast.error(t('estimates.duplicateFailed'));
    }
  };

  const handleDelete = async () => {
    const estimate = pendingDelete;
    if (!estimate || deleting) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/estimates/${estimate.estimate_id}`, {
        withCredentials: true,
      });
      setEstimates(prev => prev.filter(e => e.estimate_id !== estimate.estimate_id));
      setSelected(prev => prev.filter(s => s !== estimate.estimate_id));
      toast.success(t('estimates.deleted'));
      setPendingDelete(null);
    } catch (err) {
      logger.warn('[SavedEstimatesPage] Delete failed:', err?.message || err);
      toast.error(t('estimates.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const handleShare = async (id) => {
    setSharing(id);
    try {
      const res = await axios.post(`${API}/estimates/${id}/share`, {}, {
        withCredentials: true,
      });
      const token = res.data.share_token;
      const url = `${window.location.origin}/shared/${token}`;
      setShareUrl(url);
      setShareOpen(true);
      setCopied(false);
    } catch (err) {
      logger.warn('[SavedEstimatesPage] Share failed:', err?.message || err);
      toast.error(t('estimates.shareFailed'));
    } finally {
      setSharing(null);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(t('estimates.linkCopied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.warn('[SavedEstimatesPage] Clipboard copy failed:', err?.message || err);
      toast.error(t('estimates.copyFailed'));
    }
  };

  const handleExportPdf = (est) => {
    generateEstimatePDF({
      name: est.name,
      config: est.config,
      results: est.results,
      createdAt: est.created_at,
      tco: est.tco_snapshot,
      t,
    });
    toast.success(t('estimates.pdfDownloaded'));
  };

  const handleOpenEstimate = (est) => {
    navigate(localePath('/calculator'), {
      state: {
        estimateConfig: est.config,
        estimateName: est.name,
        tcoAssumptions: tcoOverridesOf(est),
      },
    });
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(s => s !== id);
      if (prev.length >= 2) return [prev[1], id];
      return [...prev, id];
    });
  };

  const compareEstimates = selected.map(id => estimates.find(e => e.estimate_id === id)).filter(Boolean);

  return (
    <AppShell>
      <div className="workspace-main-width">
        <PageHeader compact title={t('estimates.title')} testId="saved-estimates-heading"
          description={estimates.length > 0
                ? t('estimates.countMessage', { count: estimates.length })
                : t('estimates.noEstimates')} actions={<>
            {selected.length === 2 && (
              <Button
                onClick={() => setCompareOpen(true)}
                className="bg-[#527F19] hover:bg-[#3F6114] text-white shadow-card transition-colors"
                data-testid="compare-btn"
              >
                <GitCompareArrows className="w-4 h-4 mr-2" /> {t('estimates.compare')}
              </Button>
            )}
              <ActionLink to="/calculator"
                variant="outline"
                className="border-[#DDE1E5] bg-white text-[#3A4249] hover:text-[#527F19] hover:border-[#527F19]/40 hover:bg-white transition-colors"
                data-testid="new-estimate-btn"
              >
                <Calculator className="w-4 h-4 mr-2" /> {t('estimates.newEstimate')}
              </ActionLink>
          </>} />

        {isDemo && (
          <Card className="bg-[#B7791F]/[0.06] border-[#B7791F]/25 mb-6" data-testid="demo-estimates-banner">
            <CardContent className="py-3 flex items-center gap-3">
              <AlertTriangle className="w-4 h-4 text-[#B7791F] shrink-0" />
              <p className="text-xs text-[#8A5A0B]">
                {t('estimates.demoBanner')}{' '}
                <LocaleLink to="/auth" className="underline font-semibold hover:text-[#6E4708]">{t('estimates.createFree')}</LocaleLink> {t('estimates.toSaveOwn')}
              </p>
            </CardContent>
          </Card>
        )}

        {(() => {
          if (loading) return (
          <div className="flex justify-center py-20" data-testid="estimates-loading">
            <Loader2 className="w-8 h-8 animate-spin text-[#527F19]" />
          </div>
          );
          if (estimates.length === 0) return (
          <EmptyState testId="no-estimates-message" title={t('estimates.noEstimates')} icon={FileText}>
                <ActionLink to="/calculator" data-testid="go-to-calculator-btn">
                  <Calculator className="w-4 h-4 mr-2" /> {t('nav.calculator')}
                </ActionLink>
          </EmptyState>
          );
          return (
          <div className="grid gap-4">
            {estimates.map(est => (
              <EstimateCard
                key={est.estimate_id}
                estimate={est}
                isSelected={selected.includes(est.estimate_id)}
                isDemo={isDemo}
                sharing={sharing}
                onToggleSelect={toggleSelect}
                onOpen={handleOpenEstimate}
                onExportPdf={handleExportPdf}
                onShare={handleShare}
                onDelete={() => setPendingDelete(est)}
                onDuplicate={handleDuplicate}
              />
            ))}
          </div>
          );
        })()}
      </div>

      <CompareEstimatesDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        estimates={compareEstimates}
      />

      <ShareEstimateDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        shareUrl={shareUrl}
        copied={copied}
        onCopy={handleCopy}
      />

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => { if (!open && !deleting) setPendingDelete(null); }}
      >
        <AlertDialogContent className="bg-white border-[#DDE1E5]" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#171A1D]">{t('estimates.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-[#66707A]">
              {t('estimates.deleteBody', { name: pendingDelete?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-[#DDE1E5] text-[#3A4249] hover:bg-[#F5F6F7]"
              data-testid="delete-cancel-btn"
            >
              {t('estimates.deleteCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={deleting}
              className="bg-[#B42318] hover:bg-[#8F1C13] text-white"
              data-testid="delete-confirm-btn"
            >
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('estimates.deleteConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
